import React, { useState } from "react";

const OPCIONES_ESTADO = [
  { value: "presente", label: "Presente", icon: "bi-check-circle-fill", clase: "text-success" },
  { value: "virtual", label: "Presente (recibió la clase virtual)", icon: "bi-camera-video-fill", clase: "text-info" },
  { value: "ausente", label: "Ausente", icon: "bi-x-circle-fill", clase: "text-danger" },
  { value: "justificado", label: "Justificado", icon: "bi-shield-fill-check", clase: "text-warning" },
];

const formatFecha = (fecha) => {
  if (!fecha) return "";
  const [y, m, d] = String(fecha).split("-");
  return `${d}/${m}/${y}`;
};

function ModalConfirmarQuitar({ estudiante, onConfirmar, onCancelar }) {
  if (!estudiante) return null;
  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.45)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h6 className="modal-title fw-bold">
              <i className="bi bi-eraser-fill text-danger me-2" />
              Quitar marca de asistencia
            </h6>
            <button type="button" className="btn-close" onClick={onCancelar} />
          </div>
          <div className="modal-body pt-2">
            <p className="mb-0 text-muted small">
              ¿Quitar la marca de asistencia de <strong>{estudiante.nombre_completo}</strong> en esta sesión?
              Vuelve a quedar "sin registro".
            </p>
          </div>
          <div className="modal-footer border-0 pt-1 gap-2">
            <button className="btn btn-secondary btn-sm" onClick={onCancelar}>Cancelar</button>
            <button className="btn btn-sm btn-danger" onClick={onConfirmar}>Quitar marca</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal de una sesión puntual: lista completa de estudiantes para justificar/editar
// de un tirón. Se abre tanto desde el encabezado de una columna (fecha) como desde
// una celda individual (foco en ese estudiante). Cada cambio se guarda al toque,
// sin botón "Guardar todo" — feedback inmediato por fila.
export default function AsistenciaSesionModal({
  sesion,
  estudiantes,
  puedeEditar,
  onGuardar,
  onEliminar,
  onCerrar,
  focusEstudianteId,
}) {
  const [guardandoId, setGuardandoId] = useState(null);
  const [observaciones, setObservaciones] = useState({});
  const [confirmarQuitar, setConfirmarQuitar] = useState(null); // estudiante seleccionado

  const handleCambiar = async (estudianteId, estado) => {
    if (!puedeEditar) return;
    setGuardandoId(estudianteId);
    try {
      await onGuardar(estudianteId, estado, observaciones[estudianteId] || "");
    } finally {
      setGuardandoId(null);
    }
  };

  const handleConfirmarEliminar = async () => {
    const estudianteId = confirmarQuitar?.estudiante_id;
    setConfirmarQuitar(null);
    if (!estudianteId) return;
    setGuardandoId(estudianteId);
    try {
      await onEliminar?.(estudianteId);
    } finally {
      setGuardandoId(null);
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-calendar-check me-2" />
              Asistencia — {formatFecha(sesion.fecha)}
            </h5>
            <button type="button" className="btn-close" onClick={onCerrar} />
          </div>

          <div className="modal-body">
            {!puedeEditar && (
              <div className="alert alert-secondary py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
                <i className="bi bi-eye me-1" /> Solo lectura — no tienes permiso para justificar asistencia.
              </div>
            )}

            <div className="d-flex flex-column gap-2">
              {estudiantes.map((est) => {
                const celda = est.celdas[sesion.id] || { estado: "pendiente", origen: "sin_registro" };
                const esFoco = String(est.estudiante_id) === String(focusEstudianteId);
                const observacionActual = observaciones[est.estudiante_id] ?? celda.observacion ?? "";
                // "Virtual" es para un presencial que excepcionalmente recibió la
                // clase a distancia — un online ya se rastrea con "visto" automático.
                const opciones = est.modalidad === "online"
                  ? OPCIONES_ESTADO.filter((op) => op.value !== "virtual")
                  : OPCIONES_ESTADO;
                const tieneMarcaManual = celda.origen === "manual";
                return (
                  <div
                    key={est.estudiante_id}
                    className={`d-flex flex-wrap align-items-center gap-2 p-2 rounded ${esFoco ? "border border-primary bg-light" : "border"}`}
                  >
                    <div className="me-auto">
                      <div className="fw-semibold">{est.nombre_completo}</div>
                      <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                        {est.modalidad === "online" ? (
                          <span><i className="bi bi-camera-video me-1" />Online</span>
                        ) : (
                          <span><i className="bi bi-person me-1" />Presencial</span>
                        )}
                        {celda.origen === "automatico" && " · visto automáticamente en Campus"}
                        {celda.origen === "sin_registro" && " · sin registro"}
                      </div>
                    </div>

                    {puedeEditar && (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 220 }}
                        placeholder="Observación (opcional)"
                        value={observacionActual}
                        disabled={guardandoId === est.estudiante_id}
                        onChange={(e) =>
                          setObservaciones((prev) => ({ ...prev, [est.estudiante_id]: e.target.value }))
                        }
                      />
                    )}
                    {!puedeEditar && celda.observacion && (
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>{celda.observacion}</span>
                    )}

                    <div className="btn-group btn-group-sm" role="group">
                      {opciones.map((op) => {
                        const activo = celda.estado === op.value;
                        return (
                          <button
                            key={op.value}
                            type="button"
                            className={`btn ${activo ? "btn-secondary" : "btn-outline-secondary"}`}
                            disabled={!puedeEditar || guardandoId === est.estudiante_id}
                            onClick={() => handleCambiar(est.estudiante_id, op.value)}
                            title={op.label}
                          >
                            {guardandoId === est.estudiante_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className={`bi ${op.icon} ${activo ? "" : op.clase}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {puedeEditar && tieneMarcaManual && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={guardandoId === est.estudiante_id}
                        onClick={() => setConfirmarQuitar(est)}
                        title="Quitar marca (volver a sin registro)"
                      >
                        <i className="bi bi-eraser-fill" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <ModalConfirmarQuitar
        estudiante={confirmarQuitar}
        onConfirmar={handleConfirmarEliminar}
        onCancelar={() => setConfirmarQuitar(null)}
      />
    </div>
  );
}
